import React, { useState, useEffect } from "react";
import { useActivityFeed } from "../hooks/useActivityFeed";
import "../styles/ActivityFeed.css";

interface ActivityFeedProps {
  maxItems?: number;
  showHeader?: boolean;
}

export function ActivityFeed({
  maxItems = 15,
  showHeader = true,
}: ActivityFeedProps) {
  const { items, loading, error, clearError } = useActivityFeed(maxItems);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => clearError(), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  const getActivityIcon = (
    type: "ISSUE" | "CLAIM" | "REDEEM" | "REFUND" | "REVERSE" | "BLOCK" | "UNBLOCK" | "EXPIRE"
  ): string => {
    switch (type) {
      case "ISSUE":
        return "➕";
      case "CLAIM":
        return "🏷️";
      case "REDEEM":
        return "💳";
      case "REFUND":
        return "↩️";
      case "REVERSE":
        return "🔄";
      case "BLOCK":
        return "🚫";
      case "UNBLOCK":
        return "✅";
      case "EXPIRE":
        return "⏱️";
      default:
        return "📝";
    }
  };

  const formatTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) {
      return "just now";
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  const handleToggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  if (loading) {
    return (
      <div className="activity-feed activity-feed-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading activity feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-feed">
      {showHeader && (
        <div className="activity-feed-header">
          <h3>📊 Activity Feed</h3>
          <div className="activity-controls">
            <label className="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={handleToggleAutoRefresh}
                aria-label="Auto-refresh activity feed"
              />
              <span>Auto-refresh</span>
            </label>
          </div>
        </div>
      )}

      {error && (
        <div className="activity-feed-error">
          <span>⚠️ {error}</span>
          <button onClick={clearError} className="btn-close">
            ✕
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="activity-feed-empty">
          <p>No activity yet</p>
          <span className="empty-icon">🤷</span>
        </div>
      ) : (
        <div className="activity-feed-list">
          {items.map((item) => (
            <div key={item.id} className={`activity-item activity-${item.type.toLowerCase()}`}>
              <div className="activity-icon">{getActivityIcon(item.type)}</div>
              <div className="activity-content">
                <div className="activity-description">{item.description}</div>
                {item.reason && (
                  <div className="activity-detail">
                    <span className="label">Reason:</span>
                    <span className="value">{item.reason}</span>
                  </div>
                )}
              </div>
              <div className="activity-time">{formatTime(item.createdAt)}</div>
            </div>
          ))}
        </div>
      )}

      {!showHeader && items.length > 0 && (
        <div className="activity-feed-footer">
          <small>
            Last updated: {items.length > 0 && formatTime(items[0].createdAt)}
          </small>
        </div>
      )}
    </div>
  );
}
